/*

https://github.com/dance2die/react-use-localstorage

MIT License

Copyright (c) 2018 Sung Kim

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
 */

import { CurrentIdentity } from '@fox/extensions/src/CurrentIdentity';
import React, { Dispatch, useCallback, useState } from 'react';
import { strIsNullOrEmpty } from '@fox/shared';

export default function useLocalStorage(key: string, initialValue: string = ''): [string, Dispatch<string>] {
	const [item, setValue] = React.useState(() => {
		if (typeof window === 'undefined') return initialValue;
		const value = window.localStorage.getItem(key) || initialValue;
		window.localStorage.setItem(key, value);
		return value;
	});

	const setItem = (newValue: string) => {
		setValue(newValue);
		window.localStorage.setItem(key, newValue);
	};

	return [item, setItem];
}

// export function useTypedLocalStorage<T>(key: string, initialValue: T): [T, Dispatch<T>] {
//     const [state, setState] = useLocalStorage(key, JSON.stringify(initialValue));
//     try {
//         const s = !strIsNullOrEmpty(state) ? JSON.parse(state) : undefined;
//         return [s, st => setState(JSON.stringify(st))];
//     } catch (e) {
//         return [undefined, st => setState(JSON.stringify(st))];
//     }
// }

function init<T>(funcOrVal: T | (() => T)): T {
	if (typeof funcOrVal !== 'function') return funcOrVal;
	// funcOrVal is typed as
	//    funcOrVal: (() => T) | (T & Function)
	// I don't know why so as anying it
	return (funcOrVal as any)();
}

/** The setter is in a callback so will have a constant identity */
export function useTypedLocalStorage<T>(key: string | undefined, initialValue: T | (() => T)): [T, Dispatch<T>] {
	// State to store our value
	// Pass initial state function to useState so logic is only executed once
	const [storedValue, setStoredValue] = useState(() => {
		try {
			// Get from local storage by key
			const item = key ? window.localStorage.getItem(key) : undefined;
			// Parse stored json or if none return initialValue
			return !strIsNullOrEmpty(item) ? JSON.parse(item) : init(initialValue);
		} catch (error) {
			// If error also return initialValue
			console.log(error);
			return init(initialValue);
		}
	});

	// Return a wrapped version of useState's setter function that ...
	// ... persists the new value to localStorage.
	// important: this needs a constant identity. we are relying on setStoredValue, which will itself have a
	// static identity, but the setValue function being returned might be used in a useEffect dependency array
	// and useCallback handles this case nicely
	const setValue = useCallback((value: T | ((val: T) => void)) => {
		try {
			// Allow value to be a function so we have same API as useState
			const valueToStore = value instanceof Function ? value(storedValue) : value;
			// Save state
			setStoredValue(valueToStore);
			// Save to local storage
			if (key) {
				if (valueToStore !== undefined && valueToStore !== null) {
					window.localStorage.setItem(key, JSON.stringify(valueToStore));
				} else window.localStorage.removeItem(key);
			}
		} catch (error) {
			// A more advanced implementation would handle the error case
			console.log('Error parsing JSON', error);
		}
	}, []);

	return [storedValue, setValue];
}

/**
 * Same as useTypedLocalStorage except the key is suffixed with the tenant name.
 * Use this when storing values that are unique to a particular tenant, such as Post Hashtags.
 */
export function useTenantLocalStorage<T>(key: string, initialValue: T | (() => T)): [T, Dispatch<T>] {
	const suffixedKey = `${key}-${CurrentIdentity?.user?.t}`;
	const [storedValue, setValue] = useTypedLocalStorage<T>(suffixedKey, initialValue);

	return [storedValue, setValue];
}
